import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Textarea,
  Text as ChakraText,
  VStack,
  Select,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../hooks/useToast'
import { colors, IKReactSelect } from '../../../ui-kit'
import { countryCodes } from '../../../utils/countries'
import type {
  GetAdvancedSecuritySettingsErrorResponse,
  GetAdvancedSecuritySettingsResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import CustomPlanCTA from '../../components/CustomPlan'
import { FeaturesEnum } from '../../../utils/features'

interface FormProps {
  ipv4Blocklist: string
  refererRestrictionType: string
  refererList: string
  geoRestrictionType: string
  geoRestrictionList: Array<{
    label: string
    value: string
  }>
  userAgentList: string
  userAgentBlockList: string
  acceptHeader: string
}

const SecuritySettingsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const {
    request: getRequest,
    response: getResponse,
    loading: getLoading,
  } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const initialValues = {
    ipv4Blocklist: '',
    refererRestrictionType: 'none',
    refererList: '',
    geoRestrictionType: 'none',
    geoRestrictionList: [],
    userAgentList: '',
    userAgentBlockList: '',
    acceptHeader: '1',
  }
  const [securitySettingsFormValues, setSecuritySettingsFormValues] =
    useState<FormProps>(initialValues)
  const [formError, setFormError] = useState('')
  const [formInfo, setFormInfo] = useState('')
  const [disableFields, setDisableFields] = useState(false)
  const [countrylistValues, setCountrylistValues] = useState<
    Array<{
      label: string
      value: string
    }>
  >([])

  const getSecuritySettings = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      user.pricingPlan.featuresList.includes(FeaturesEnum.SECURITY)
    ) {
      const res = await getRequest.get(
        `/api/clients/${user.clientNumber}/settings/advanced-security`,
      )

      if (getRequest.error) {
        if (getResponse.status === 404) {
          setFormInfo(
            `${(res as GetAdvancedSecuritySettingsErrorResponse).message} ${
              (res as GetAdvancedSecuritySettingsErrorResponse).help
            }`,
          )
          setDisableFields(true)
        } else {
          setFormError(
            t(
              'unknown_error_security_settings',
              'An unknown error occured in fetching security settings',
            ),
          )
        }
        return
      }

      const countryList: Array<{
        label: string
        value: string
      }> = countryCodes.filter(countryCode =>
        (
          res as GetAdvancedSecuritySettingsResponse
        ).geoRestriction.items.includes(countryCode.value),
      )

      const fetchedSecuritySettingValues = {
        ipv4Blocklist: (
          res as GetAdvancedSecuritySettingsResponse
        ).ipv4Blocklist.join('\n'),
        refererRestrictionType: (res as GetAdvancedSecuritySettingsResponse)
          .refererRestriction.restrictionType,
        refererList: (
          res as GetAdvancedSecuritySettingsResponse
        ).refererRestriction.items.join('\n'),
        geoRestrictionType: (res as GetAdvancedSecuritySettingsResponse)
          .geoRestriction.restrictionType,
        geoRestrictionList: countryList,
        userAgentList: (
          res as GetAdvancedSecuritySettingsResponse
        ).userAgentList.join('\n'),
        acceptHeader: (res as GetAdvancedSecuritySettingsResponse).acceptHeader,
        userAgentBlockList: (
          res as GetAdvancedSecuritySettingsResponse
        ).userAgentBlockList.join('\n'),
      }

      setSecuritySettingsFormValues(fetchedSecuritySettingValues)
      setCountrylistValues(countryList)
      setFormError('')
    }
  }

  useEffect(() => {
    void getSecuritySettings()
  }, [user])

  if (getLoading) {
    return (
      <Center height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  return (
    <Formik
      enableReinitialize
      initialValues={securitySettingsFormValues}
      onSubmit={async (values, { setFieldError }): Promise<void> => {
        setFormError('')
        const ipv4Blocklist: string[] = !values.ipv4Blocklist
          ? []
          : values.ipv4Blocklist.trim().split('\n')
        const userAgentList: string[] = !values.userAgentList
          ? []
          : values.userAgentList.trim().split('\n')
        const userAgentBlockList: string[] = !values.userAgentBlockList
          ? []
          : values.userAgentBlockList.trim().split('\n')
        const refererList = !values.refererList
          ? []
          : values.refererList.trim().split('\n')
        const refererRestriction = {
          items: values.refererRestrictionType === 'none' ? [] : refererList,
          restrictionType: values.refererRestrictionType,
        }
        const geoRestriction = {
          items:
            values.geoRestrictionType === 'none'
              ? []
              : values.geoRestrictionList.map(item => item.value),
          restrictionType: values.geoRestrictionType,
        }
        if (ipv4Blocklist.length > 100) {
          setFieldError(
            'ipv4Blocklist',
            t(
              'ip_limit_exceeded',
              'Total IP/IP range is greater than the allowed limit of 100.',
            ),
          )
          return
        }

        if (userAgentList.length > 10) {
          setFieldError(
            'userAgentList',
            t(
              'user_agent_limit_exceeded',
              'Total user agents are more than the allowed limit of 10.',
            ),
          )
          return
        }

        if (userAgentBlockList.length > 10) {
          setFieldError(
            'userAgentBlockList',
            t(
              'user_agent_limit_exceeded',
              'Total user agents are more than the allowed limit of 10.',
            ),
          )
          return
        }

        if (
          values.refererRestrictionType !== 'none' &&
          refererList.length === 0
        ) {
          setFieldError(
            'refererList',
            t(
              'must_include_one_referer',
              'Must include at least one referer in the list.',
            ),
          )
          return
        }

        if (!refererList.every(item => item.length < 70)) {
          setFieldError(
            'refererList',
            t(
              'referer_length_exceeded',
              'Every referer must have length less than 70.',
            ),
          )
          return
        }

        if (
          values.geoRestrictionType !== 'none' &&
          values.geoRestrictionList.length === 0
        ) {
          setFieldError(
            'geoRestrictionList',
            t(
              'must_include_one_country',
              'Must include at least one country in the list.',
            ),
          )
          return
        }
        const res = await request.post(
          `/api/clients/${user?.clientNumber}/settings/advanced-security`,
          {
            ipv4Blocklist,
            refererRestriction,
            geoRestriction,
            userAgentList,
            userAgentBlockList,
            acceptHeader: values.acceptHeader,
          },
        )

        if (request.error) {
          if (response.status === 404 || response.status === 400) {
            setFormError(
              (res as GetAdvancedSecuritySettingsErrorResponse).message,
            )
          } else {
            setFormError(
              t(
                'unknown_error_update_security_settings',
                'An unknown error occured in updating security settings',
              ),
            )
          }
          return
        }

        const countryList: Array<{
          label: string
          value: string
        }> = countryCodes.filter(countryCode =>
          (
            res as GetAdvancedSecuritySettingsResponse
          ).geoRestriction.items.includes(countryCode.value),
        )

        const fetchedSecuritySettingValues = {
          ipv4Blocklist: (
            res as GetAdvancedSecuritySettingsResponse
          ).ipv4Blocklist.join('\n'),
          refererRestrictionType: (res as GetAdvancedSecuritySettingsResponse)
            .refererRestriction.restrictionType,
          refererList: (
            res as GetAdvancedSecuritySettingsResponse
          ).refererRestriction.items.join('\n'),
          geoRestrictionType: (res as GetAdvancedSecuritySettingsResponse)
            .geoRestriction.restrictionType,
          geoRestrictionList: countryList,
          userAgentList: (
            res as GetAdvancedSecuritySettingsResponse
          ).userAgentList.join('\n'),
          acceptHeader: (res as GetAdvancedSecuritySettingsResponse)
            .acceptHeader,
          userAgentBlockList: (
            res as GetAdvancedSecuritySettingsResponse
          ).userAgentBlockList.join('\n'),
        }

        setSecuritySettingsFormValues(fetchedSecuritySettingValues)
        setCountrylistValues(countryList)
        setFormError('')

        toast({
          description: t(
            'security_settings_updated_successfully',
            'Security settings updated successfully',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }}
    >
      {({
        dirty,
        isSubmitting,
        values,
        resetForm,
        submitForm,
        handleChange,
        setFieldValue,
      }) => (
        <>
          {user?.pricingPlan.featuresList.includes(FeaturesEnum.SECURITY) ? (
            <MainContentHeadingBox reverse={true}>
              <HStack spacing={4}>
                {dirty && (
                  <Button
                    onClick={() => {
                      void resetForm()
                      setCountrylistValues(
                        securitySettingsFormValues.geoRestrictionList,
                      )
                    }}
                    variant="outline"
                    isDisabled={isSubmitting || disableFields}
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                )}
                <Button
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                  isDisabled={!dirty || disableFields}
                >
                  {t('save', 'Save')}
                </Button>
              </HStack>
            </MainContentHeadingBox>
          ) : (
            <MainContentHeadingBox>
              <Stack direction={['column']} spacing={1}>
                <Heading
                  as="h2"
                  size="md"
                  fontWeight="600"
                  color={colors.appDarkIndigo}
                >
                  {t('advanced_security', 'Advanced Security')}
                </Heading>
                <ChakraText color="appBattleshipGrey">
                  {t(
                    'advanced_security_description',
                    'Block IP/IP ranges, restrict referrers, optimize delivery, and apply Geo-restrictions on file delivery using these settings.',
                  )}
                </ChakraText>
              </Stack>
            </MainContentHeadingBox>
          )}

          <MainContentWrapper>
            {user?.pricingPlan.featuresList.includes(FeaturesEnum.SECURITY) ? (
              <>
                {' '}
                {formInfo ? (
                  <Alert status="info" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formInfo}</AlertDescription>
                  </Alert>
                ) : null}
                {formError ? (
                  <Alert status="error" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="ipv4Blocklist">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isInvalid={Boolean(
                        form.errors.ipv4Blocklist && form.touched.ipv4Blocklist,
                      )}
                    >
                      <FormLabel htmlFor="ipv4Blocklist">
                        {t('ip_blocklist_label', 'IP or IP range blocklist')}
                      </FormLabel>
                      <ChakraText paddingBottom="0.8em">
                        {t(
                          'ip_blocklist_textarea_placeholder',
                          'Enter at most 100 IPs or IP ranges (v4), separated by a new line.',
                        )}
                      </ChakraText>
                      <Textarea
                        {...field}
                        id="ipv4Blocklist"
                        isDisabled={disableFields}
                        onChange={handleChange}
                        rows={5}
                      />
                      {form.errors.ipv4Blocklist &&
                      form.touched.ipv4Blocklist ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.ipv4Blocklist}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="userAgentList">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.userAgentList && form.touched.userAgentList,
                      )}
                    >
                      <FormLabel htmlFor="userAgentList">
                        {t(
                          'user_agent_label',
                          'User agents for optimized asset delivery',
                        )}
                      </FormLabel>
                      <ChakraText paddingBottom="0.8em">
                        {t(
                          'user_agent_textarea_placeholder',
                          'Enter at most 10 user agents, separated by a new line.',
                        )}
                      </ChakraText>
                      <Textarea
                        {...field}
                        id="userAgentList"
                        isDisabled={disableFields}
                        onChange={handleChange}
                        rows={5}
                      />
                      {form.errors.userAgentList &&
                      form.touched.userAgentList ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userAgentList}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="acceptHeader">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.acceptHeader && form.touched.acceptHeader,
                      )}
                    >
                      <FormLabel>
                        {t(
                          'accept_header_user_agent',
                          'Accept header for configured user agents',
                        )}
                      </FormLabel>
                      <Select
                        {...field}
                        onChange={e => {
                          form.setFieldValue(field.name, e.target.value)
                        }}
                      >
                        <option value="1">
                          image/webp,image/apng,*/*;q=0.8
                        </option>
                        <option value="2">
                          image/webp,image/avif,image/apng,*/*;q=0.8
                        </option>
                      </Select>
                      {form.errors.acceptHeader && form.touched.acceptHeader ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.acceptHeader}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="geoRestrictionType">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.geoRestrictionType &&
                          form.touched.geoRestrictionType,
                      )}
                      isRequired
                    >
                      <FormLabel>
                        {t('geographic_restriction', 'Geographic restriction')}
                      </FormLabel>
                      <RadioGroup
                        {...field}
                        isDisabled={disableFields}
                        value={form.values.geoRestrictionType}
                      >
                        <VStack
                          spacing="1em"
                          direction="column"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Radio value="none" onChange={form.handleChange}>
                              {t('no_restrictions', 'No restrictions')}
                            </Radio>
                          </Box>
                          <Box>
                            <Radio
                              value="blacklist"
                              onChange={form.handleChange}
                            >
                              {t('blocklist', 'Blocklist')}
                            </Radio>
                          </Box>
                          <Box>
                            <Radio
                              value="whitelist"
                              onChange={form.handleChange}
                            >
                              {t('allowlist', 'Allowlist')}
                            </Radio>
                          </Box>
                        </VStack>
                      </RadioGroup>
                      {form.errors.geoRestrictionType ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.geoRestrictionType}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="geoRestrictionList">
                  {({ form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(form.errors.geoRestrictionList)}
                      isRequired={form.values.geoRestrictionType !== 'none'}
                    >
                      <FormLabel>{t('countries', 'Countries')}</FormLabel>
                      <IKReactSelect
                        menuPlacement="auto"
                        variant="compact"
                        placeholder={t('select_countries', 'Select countries')}
                        options={countryCodes}
                        value={countrylistValues}
                        onChange={countries => {
                          setFieldValue('geoRestrictionList', countries)
                          setCountrylistValues(countries)
                        }}
                        isDisabled={
                          values.geoRestrictionType === 'none' || disableFields
                        }
                        isMulti
                        isClearable
                      />
                      {form.errors.geoRestrictionList ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.geoRestrictionList}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="refererRestrictionType">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.refererRestrictionType &&
                          form.touched.refererRestrictionType,
                      )}
                      isRequired
                    >
                      <FormLabel>
                        {t('referer_restriction', 'Referer restriction')}
                      </FormLabel>
                      <RadioGroup
                        {...field}
                        isDisabled={disableFields}
                        value={form.values.refererRestrictionType}
                      >
                        <VStack
                          spacing="1em"
                          direction="column"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Radio value="none" onChange={form.handleChange}>
                              {t('no_restrictions', 'No restrictions')}
                            </Radio>
                          </Box>
                          <Box>
                            <Radio
                              value="blacklist"
                              onChange={form.handleChange}
                            >
                              {t('blocklist', 'Blocklist')}
                            </Radio>
                          </Box>
                          <Box>
                            <Radio
                              value="whitelist"
                              onChange={form.handleChange}
                            >
                              {t('allowlist', 'Allowlist')}
                            </Radio>
                          </Box>
                        </VStack>
                      </RadioGroup>
                      {form.errors.refererRestrictionType &&
                      form.touched.refererRestrictionType ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.refererRestrictionType}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="refererList">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isInvalid={Boolean(
                        form.errors.refererList && form.touched.refererList,
                      )}
                      isRequired={form.values.refererRestrictionType !== 'none'}
                    >
                      <FormLabel htmlFor="refererList">
                        {t('referers', 'Referers')}
                      </FormLabel>
                      <ChakraText paddingBottom="0.8em">
                        {t(
                          'refer_textarea_placeholder',
                          'Enter at most 30 referers separated by a new line.',
                        )}
                      </ChakraText>
                      <Textarea
                        {...field}
                        id="refererList"
                        isDisabled={
                          form.values.refererRestrictionType === 'none' ||
                          disableFields
                        }
                        onChange={handleChange}
                        rows={5}
                      />
                      {form.errors.refererList && form.touched.refererList ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.refererList}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="userAgentBlockList">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.userAgentBlockList &&
                          form.touched.userAgentBlockList,
                      )}
                    >
                      <FormLabel htmlFor="userAgentBlockList">
                        {t(
                          'user_agent_block_label',
                          'User agents to be blocked',
                        )}
                      </FormLabel>
                      <ChakraText paddingBottom="0.8em">
                        {t(
                          'user_agent_textarea_placeholder',
                          'Enter at most 10 user agents, separated by a new line.',
                        )}
                      </ChakraText>
                      <Textarea
                        {...field}
                        id="userAgentBlockList"
                        isDisabled={disableFields}
                        onChange={handleChange}
                        rows={5}
                      />
                      {form.errors.userAgentBlockList &&
                      form.touched.userAgentBlockList ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userAgentBlockList}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
              </>
            ) : (
              <CustomPlanCTA feature={FeaturesEnum.SECURITY} />
            )}
          </MainContentWrapper>
        </>
      )}
    </Formik>
  )
}

export default SecuritySettingsLayout
